import { BACKEND_URL } from '../config_and_helpers/config';
import axios from 'axios';
import { showToast } from '../config_and_helpers/helpers';
import { logError, logInfo } from '../config_and_helpers/logger';
import { isCanceledRequest } from '../utils/listSearchRequest';

const LOG_CONTEXT = 'notifications_client';

/**
 * Normalize delete/clear-all error payloads for DeleteButton and callers.
 *
 * @param {import('axios').AxiosError} error
 * @returns {{ success: false, status?: number, detail: string, error: string }}
 */
function normalizeNotificationMutationError(error) {
  const status = error.response?.status;
  const data = error.response?.data || {};
  const detail =
    data.detail ||
    data.error ||
    (typeof data === 'string' ? data : error.message);
  return { success: false, status, detail, error: detail };
}

/**
 * Fetches order notifications from the backend API.
 *
 * @param {string} token - The authentication token.
 * @param {function} setOrderNotifications - The function to set the order notifications.
 * @param {object} options - Optional parameters.
 * @param {string} options.searchInput - The search input provided by the user.
 * @param {string} options.supplierId - The supplier ID.
 * @param {string} options.nextPage - The URL of the next page.
 * @returns {Promise<{ success: boolean, nextPage: string }>} The success status and the URL of the next page.
 * @throws {Error} If an error occurs during the request.
 */
export const getOrderNotifications = async (
  token,
  setOrderNotifications,
  options = {}
) => {
  const { searchInput = '', supplierId = '', nextPage = null, signal = undefined } =
    options;

  // Construct the request URL, initially pointing to first page of order notifications
  let url = nextPage
    ? nextPage
    : `${BACKEND_URL}order_notifications/?page_num=1`;

  // Add a search parameter to the URL if there's any search input given by user
  if (searchInput) {
    url += `&search=${searchInput}`;
  }
  if (supplierId) {
    url += `&supplier_id=${supplierId}`;
  }

  try {
    // Send a GET request to fetch the list of order notifications
    const response = await axios.get(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
      signal,
    });

    // Extract 'next' object from response data for pagination
    const nextCursor = response.data.next;

    // If there's no nextCursor available, then either it's a nextPage request or end of order notifications list
    // is reached in both cases update setNotifications accordingly
    if (!nextCursor) {
      if (nextPage) {
        setOrderNotifications(prevOrderNotifications => [
          ...prevOrderNotifications,
          ...response.data.results,
        ]);
      } else {
        setOrderNotifications(response.data.results);
      }
      return { success: true, reachedEnd: true };
    }

    // If it's a nextPage request, then merge old results with new ones
    // Otherwise update all processes with new results
    if (!nextPage) {
      setOrderNotifications(response.data.results);
    } else {
      setOrderNotifications(prevOrderNotifications => [
        ...prevOrderNotifications,
        ...response.data.results,
      ]);
    }

    // If there are more order notifications to be displayed in next pages return the
    // success status as true and the url for next page of order notifications
    return { success: true, nextPage: response.data.next };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    // Log and return any errors that occurred during the request
    logError('notifications_client', 'getOrderNotifications failed', error.response?.data ?? error);
    return Object.values(error.response?.data ?? {});
  }
};

/**
 * Deletes an order notification from the backend.
 *
 * @async
 * @param {string} token - The user's authentication token.
 * @param {number} notificationId - The ID of the notification to delete.
 * @returns {Promise<{ success: boolean, toast?: Function, status?: number, detail?: string }>}
 */
export const deleteOrderNotification = async (token, notificationId) => {
  logInfo(LOG_CONTEXT, 'deleteOrderNotification start', { notificationId });
  try {
    await axios.delete(`${BACKEND_URL}order_notifications/${notificationId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    logInfo(LOG_CONTEXT, 'deleteOrderNotification success', { notificationId });

    return {
      success: true,
      toast: () =>
        showToast(
          'Order notification deleted successfully!',
          'success',
          'top-right',
          3000
        ),
    };
  } catch (error) {
    const normalized = normalizeNotificationMutationError(error);
    logError(LOG_CONTEXT, 'deleteOrderNotification failed', {
      notificationId,
      status: normalized.status,
      detail: normalized.detail,
    });
    return normalized;
  }
};

/**
 * Clears all order notifications for the lab (bulk reset).
 *
 * @async
 * @param {string} token
 * @returns {Promise<{ success: boolean, deletedCount?: number, toast?: Function, status?: number, detail?: string }>}
 */
export const clearAllOrderNotifications = async token => {
  logInfo(LOG_CONTEXT, 'clearAllOrderNotifications start');
  try {
    const response = await axios.delete(
      `${BACKEND_URL}order_notifications/clear_all/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    const deletedCount = response.data?.deleted_count ?? 0;
    logInfo(LOG_CONTEXT, 'clearAllOrderNotifications success', { deletedCount });

    return {
      success: true,
      deletedCount,
      toast: () =>
        showToast(
          `Cleared ${deletedCount} reorder alert${deletedCount === 1 ? '' : 's'}.`,
          'success',
          'top-right',
          3000
        ),
    };
  } catch (error) {
    const normalized = normalizeNotificationMutationError(error);
    logError(LOG_CONTEXT, 'clearAllOrderNotifications failed', {
      status: normalized.status,
      detail: normalized.detail,
    });
    return normalized;
  }
};

/**
 * Fetches detailed information for a specific order notification.
 * This is used for lazy loading when detailed information is needed.
 *
 * @async
 * @param {string} token - The user's authentication token.
 * @param {number} notificationId - The ID of the notification to get details for.
 * @returns {Promise<Object>} - A promise that resolves to the detailed notification data.
 * @throws {Error} - If an error occurs during the request.
 */
export const getOrderNotificationDetails = async (token, notificationId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}order_notifications/${notificationId}/get_details/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError(
      'notifications_client',
      'getOrderNotificationDetails failed',
      error.response?.data ?? error
    );
    return {
      success: false,
      error: error.response?.data || 'Failed to fetch notification details',
    };
  }
};
